import React, { useMemo } from "react";
import styled, { keyframes } from "styled-components";
import ModalButton from "./ModalButton";
import { AlertProps } from "types/Common";
import { colors } from "constants/colors";

// 알럿 모달
const AlertModal = ({
  isVisible,
  alertStyle,
  titleMessage,
  contentComponent,
  message,
  hideAlert,
  marginHorizontal = 40,
  okButtonStyle,
  cancelButtonText = "취소",
  okButtonText = "확인",
  okOnPress,
  cancelOnPress,
}: AlertProps) => {
  const OkButton = () =>
    useMemo(
      () => (
        <ModalButton
          hideAlert={() => hideAlert && hideAlert()}
          $backgroundColor={
            okButtonStyle === "red"
              ? "#fae0e0"
              : okButtonStyle === "gray"
                ? colors.subBody1
                : colors.subPurple2
          }
          $textColor={
            okButtonStyle === "red"
              ? colors.statusError
              : okButtonStyle === "gray"
                ? colors.subBody3
                : colors.subPurple4
          }
          buttonText={okButtonText}
          onPress={okOnPress}
        />
      ),
      [],
    );

  const CancelButton = () =>
    useMemo(
      () => (
        <ModalButton
          hideAlert={() => hideAlert && hideAlert()}
          $backgroundColor={colors.subBody1}
          $textColor={colors.subBody3}
          buttonText={cancelButtonText}
          onPress={cancelOnPress}
        />
      ),
      [],
    );

  return (
    <StyledContainer
      $isVisible={isVisible}
      $paddingHorizontal={marginHorizontal}
    >
      <StyledModalBox>
        <StyledContentWrapper>
          {/* 타이틀 */}
          <StyledTitle>{titleMessage || "안내"}</StyledTitle>
          {/* 컴포넌트/메세지 */}
          {!!contentComponent ? (
            <StyledContentComponent>{contentComponent}</StyledContentComponent>
          ) : (
            <StyledMessageWrapper>{message}</StyledMessageWrapper>
          )}
        </StyledContentWrapper>

        <StyledButtonWrapper>
          {alertStyle === "confirmAlert" ? (
            <React.Fragment>
              <CancelButton />
              <StyledPadding width={8} />
              <OkButton />
            </React.Fragment>
          ) : alertStyle === "reverseConfirmAlert" ? (
            <React.Fragment>
              <OkButton />
              <StyledPadding width={8} />
              <CancelButton />
            </React.Fragment>
          ) : (
            <OkButton />
          )}
        </StyledButtonWrapper>
      </StyledModalBox>
    </StyledContainer>
  );
};
export default AlertModal;

// animations
const fadeIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
`;

const StyledContainer = styled.div<{
  $isVisible: boolean;
  $paddingHorizontal?: number;
}>`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  visibility: ${({ $isVisible }) => ($isVisible ? "visible" : "hidden")};
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.6);
  z-index: 10;
  animation: ${({ $isVisible }) => ($isVisible ? fadeIn : fadeOut)} 0.15s
    ease-out;
  padding: 0px ${({ $paddingHorizontal }) => $paddingHorizontal || 0}px;
`;

const StyledModalBox = styled.div`
  width: 100%; // min-width, max-width 만큼 늘림
  min-width: 280px;
  max-width: 360px;
  background-color: ${colors.white};
  border-radius: 12px;
  padding-top: 24px;
  padding-bottom: 8px;
  opacity: 1;
  box-shadow: 0px 8px 7px #00000029;
`;

const StyledContentWrapper = styled.div`
  padding: 0px 20px;
  text-align: center;
`;

const StyledTitle = styled.div`
  padding-bottom: 16px;
  font-size: 17px;
  font-weight: 500;
  line-height: 25px;
  letter-spacing: -0.85px;
  text-align: center;
  color: ${colors.black};
`;

const StyledMessageWrapper = styled.div`
  padding-bottom: 24px;
  font-size: 15px;
  line-height: 23px;
  letter-spacing: -0.75px;
  text-align: center;
  color: ${colors.subBody3};
  white-space: pre-wrap;
`;

const StyledButtonWrapper = styled.div`
  display: flex;
  padding: 0px 8px 0px;
`;

const StyledContentComponent = styled.div`
  width: 100%;
  padding-bottom: 24px;
  padding-top: 16px;
`;

const StyledPadding = styled.div<{ width?: number; height?: number }>`
  height: ${({ height }) => height || 0}px;
  width: ${({ width }) => width || 0}px;
`;
